'use client';

import { useState, useEffect } from 'react';
import axios from 'axios';
import Link from 'next/link';
import NewsCard from '@/components/ui/NewsCard';
import { NewsItem } from '@/types/news';
import { ApiResponse } from '@/types/api';
import { News } from '@/types';
import { useTranslation } from '@/context/LanguageContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';

import { useUnit } from '@/context/UnitContext';

// ...

export default function NewsSection() {
  const { t, language } = useTranslation();
  const { unitId, unitQueryParams } = useUnit();
  const [newsData, setNewsData] = useState<News[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!unitId && !unitQueryParams) {
      setLoading(false);
      return;
    }

    const fetchLatestNews = async () => {
      try {
        const params = unitQueryParams || (unitId ? `unit_id=${unitId}` : '');
        const response = await axios.post<ApiResponse<NewsItem[]>>('/api/doServices', {
          method: 'GET',
          endpoint: `web/news?limit=3&page=1&type=1&${params}&order=PUBLISHED_DATE DESC`
        });

        const apiData = response.data;

        if (apiData && apiData.data) {
          const mappedNews: News[] = apiData.data.map((item: NewsItem) => ({
            id: item.id.toString(),
            title: language === 'en' ? (item.title_en || item.title_idn) : (item.title_idn || item.title_en),
            slug: item.slug,
            excerpt: language === 'en' 
              ? (item.content_en ? item.content_en.replace(/<[^>]+>/g, '').substring(0, 120) + '...' : '')
              : (item.content_idn ? item.content_idn.replace(/<[^>]+>/g, '').substring(0, 120) + '...' : ''),
            content: language === 'en' ? (item.content_en || item.content_idn) : (item.content_idn || item.content_en),
            image: `/api/doServicesProxy?endpoint=web/file/news/${item.id}&isImage=true`,
            category: item.tags?.[0]?.title || t('nav.news'),
            categoryColor: 'bg-unmulGreen text-white',
            date: new Date(item.published_date).toLocaleDateString(language === 'en' ? 'en-US' : 'id-ID', {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            }),
          }));
          setNewsData(mappedNews);
        }
      } catch (error: any) {
        console.error('❌ Error fetching news:', error.response?.data || error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestNews();
  }, [language, t, unitId]);

  return (
    <section id="berita" className="py-24 bg-white">
      <div className="container mx-auto px-6">
        <div className="flex flex-col md:flex-row justify-between items-end mb-10">
          <div>
            <h2 className="text-3xl font-bold text-gray-800">{t('news.title')}</h2>
            <div className="h-1 w-20 bg-unmulYellow mt-2"></div>
          </div>
          <div className="flex gap-2 mt-4 md:mt-0 overflow-x-auto pb-2">
            <Link href="/berita" className="px-4 py-2 bg-unmulGreen text-white rounded-full text-sm font-bold shadow-md hover:bg-unmulDarkGreen transition">
              {t('nav.news')}
            </Link>
            <Link href="/pengumuman" className="px-4 py-2 bg-gray-100 text-gray-600 hover:bg-gray-200 rounded-full text-sm font-medium transition">
              {t('news.announcement')}
            </Link>
            <Link href="/galeri" className="px-4 py-2 bg-gray-100 text-gray-600 hover:bg-gray-200 rounded-full text-sm font-medium transition">
              {t('news.gallery')}
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-unmulGreen"></div>
          </div>
        ) : newsData.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            {t('news.noNews')}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {newsData.map((news) => (
              <NewsCard key={news.id} news={news} />
            ))}
          </div>
        )}

        <div className="text-center mt-12">
          <Link href="/berita" className="inline-block px-8 py-4 bg-unmulGreen text-white font-bold rounded-lg hover:bg-unmulDarkGreen transition">
            {t('news.viewAll')} <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
          </Link>
        </div>
      </div>
    </section>
  );
}
